'use client';

import React, { useMemo } from 'react';
import Tag from './Tag';

interface TagType {
  id: number;
  name: string;
  FormattedText?: () => React.JSX.Element;
  fullText?: string;
  bgColor?: string;
  href?: string;
}

interface TagsProps {
  tags: TagType[];
  size?: 'sm' | 'md' | 'lg';
  max?: number;
  className?: string;
}

const Tags: React.FC<TagsProps> = ({
  tags,
  className = '',
  max,
  size = 'sm',
}) => {
  const tagsWithNoEmptyValues = useMemo(() => {
    return tags?.filter((tag) => tag);
  }, [tags]);

  // Calculate visible tags and remaining tags
  const { visibleTags, remainingTags } = useMemo(() => {
    if (!tagsWithNoEmptyValues?.length) {
      return {
        visibleTags: [],
        remainingTags: [],
      };
    }

    if (!max || tagsWithNoEmptyValues.length <= max) {
      return {
        visibleTags: tagsWithNoEmptyValues,
        remainingTags: [],
      };
    }

    return {
      visibleTags: tagsWithNoEmptyValues.slice(0, max),
      remainingTags: tagsWithNoEmptyValues.slice(max),
    };
  }, [tagsWithNoEmptyValues, max]);

  // Render the tags
  const renderTags = () => {
    return visibleTags.map((tag, index) => (
      <Tag
        key={tag.id || index}
        tag={tag}
        size={size}
        className={`max-w-48 overflow-hidden rounded-full mr-1 mb-1 ${
          visibleTags.length === 1 ? 'flex-1' : 'flex-shrink-0'
        }`}
      />
    ));
  };

  // Render the +X more indicator if there are remaining tags
  const renderRemainingIndicator = () => {
    if (remainingTags.length === 0) return null;

    return (
      <div className="relative">
        <Tag
          tag={{
            id: -1,
            name: `+${remainingTags.length}`,
            bgColor: '#F5F5F5',
            color: '#666666',
          }}
          size={size}
          className="cursor-pointer hover:bg-neutral-200 transition-colors duration-200"
        />
      </div>
    );
  };

  // If no valid tags, return null
  if (!tagsWithNoEmptyValues?.length) return null;

  return (
    <div
      className={`flex items-center gap-1 relative w-full overflow-hidden ${className}`}
      data-testid="pulse-tags"
    >
      {renderTags()}
      {renderRemainingIndicator()}
    </div>
  );
};

export default Tags;
